import type {VariantProps} from "class-variance-authority";

import {badgeVariants} from "@/components/ui/badge";
import {formatTime} from "@/src/modules/core/lib/formatters";
import {officerService} from "@/src/modules/officer/services";
import {findActiveShiftBreak, getShiftBreaks} from "@/src/modules/shifts/lib/shift-breaks";
import type {Shift} from "@/src/modules/shifts/types";

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>["variant"]>;

export type ActiveShiftBreakChipDisplay = {
  label: string;
  variant: BadgeVariant;
  tickMs: number;
};

/** Header chip for the signed-in officer's active shift (Dispatch desk). */
export function getActiveShiftBreakChipDisplay(
  shift: Shift | null | undefined,
  now: Date = new Date(),
): ActiveShiftBreakChipDisplay | null {
  if (!shift) return null;

  const breaks = getShiftBreaks(shift);
  if (breaks.length === 0) return null;

  const active = findActiveShiftBreak(shift);
  if (active) {
    const startedAt = active.execution_start_datetime;
    const minutes = startedAt
      ? Math.max(0, Math.floor((now.getTime() - new Date(startedAt).getTime()) / 60000))
      : null;
    const label =
      minutes !== null
        ? `On break · ${active.break_type} (${minutes}m)`
        : `On break · ${active.break_type}`;
    return {label, variant: "warning", tickMs: 60_000};
  }

  const scheduled = breaks.filter((item) => item.status === "scheduled");
  const due = scheduled.find((item) => officerService.getIsBreakAvailable(item));
  if (due) {
    return {label: "Break due", variant: "warning", tickMs: 60_000};
  }

  const nowMs = now.getTime();
  const upcoming = scheduled
    .filter(
      (item) =>
        item.execution_start_datetime &&
        new Date(item.execution_start_datetime).getTime() > nowMs,
    )
    .sort(
      (a, b) =>
        new Date(a.execution_start_datetime!).getTime() -
        new Date(b.execution_start_datetime!).getTime(),
    )[0];

  if (upcoming?.execution_start_datetime) {
    return {
      label: `Next break · ${formatTime(upcoming.execution_start_datetime)}`,
      variant: "secondary",
      tickMs: 60_000,
    };
  }

  return null;
}
